import { useState } from 'react';
import { Network, Copy, Plus, Globe, ShieldCheck, ShieldOff, Trash2, MoreHorizontal } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Label,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useNetworkList,
  useAttachNic,
  useDetachNic,
  useSecgroups,
  useAttachSecgroupToNic,
  useDetachSecgroupFromNic,
  useElasticIps,
  useAttachElasticIp,
  type Instance,
  type InstanceNicMeta,
  type Secgroup,
} from '@/api/opennebula';
import { copyToClipboard } from '../shared';

export function NetworkTab({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const { show } = useToast();
  const nics = instance.metadata?.nics ?? [];
  const aliases = instance.metadata?.nic_alias ?? [];
  // Security groups are scoped per NIC (nic.security_groups = secgroup ids);
  // resolve ids → names from the service's secgroup list.
  const { data: allGroups = [] } = useSecgroups(serviceId);
  const groupName = (id: string) => allGroups.find((g) => String(g.id) === String(id))?.name || id;
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachFipOpen, setAttachFipOpen] = useState(false);
  const [detachNic, setDetachNic] = useState<InstanceNicMeta | null>(null);
  // The dialog is keyed by the NIC's id, not by the NIC object: detaching a
  // security group refetches the instance, and a captured object would keep
  // showing the group that was just removed until the page was reloaded.
  const [manageNicId, setManageNicId] = useState<string | null>(null);
  const manageNic = manageNicId === null
    ? null
    : nics.find((n) => String(n.nic_id) === manageNicId) ?? null;

  const detach = useDetachNic(serviceId, instance.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'NIC detached' });
      setDetachNic(null);
    },
    onError: () => show({ tone: 'danger', title: 'Detach failed' }),
  });

  const columns: ColumnDef<InstanceNicMeta, unknown>[] = [
    {
      id: 'network',
      header: 'Network',
      cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.network || '—'}</span>,
    },
    {
      id: 'ip',
      header: 'IP address',
      cell: ({ row }) => <CopyCell value={row.original.ip} />,
    },
    {
      id: 'mac',
      header: 'MAC',
      cell: ({ row }) => (
        <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {row.original.mac || '—'}
        </span>
      ),
    },
    {
      id: 'alias',
      header: 'Floating IP',
      cell: ({ row }) => {
        const alias = aliases.find((a) => a.nic_id === row.original.alias_ids);
        return alias?.ip ? <CopyCell value={alias.ip} /> : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
      },
    },
    {
      id: 'dns',
      header: 'DNS',
      cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>{row.original.dns || '—'}</span>,
    },
    {
      id: 'secgroups',
      header: 'Security groups',
      cell: ({ row }) => {
        const ids = row.original.security_groups ?? [];
        if (ids.length === 0) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 }}>
            {ids.slice(0, 2).map((id) => (
              <Badge key={id} tone="neutral" appearance="subtle" size="sm">
                {/* Truncate long names so a verbose secgroup name can't blow out the column. */}
                <span title={groupName(id)} style={{ display: 'inline-block', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                  {groupName(id)}
                </span>
              </Badge>
            ))}
            {ids.length > 2 && <Badge tone="neutral" appearance="outline" size="sm">+{ids.length - 2}</Badge>}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
          {/* Safe, frequent action inline; destructive NIC-detach kept apart in the
              overflow menu so the two aren't confused. */}
          <Button size="sm" variant="ghost" startIcon={<ShieldCheck size={14} />} onClick={() => setManageNicId(String(row.original.nic_id ?? ""))}>
            Security groups
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="NIC actions">
                <MoreHorizontal size={16} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem tone="danger" onSelect={() => setDetachNic(row.original)}>
                Detach NIC
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>
    <Section
      title="Network interfaces"
      description="Active NICs on this instance, with their IP and physical addresses."
      actions={
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
          <Button size="sm" variant="secondary" startIcon={<Globe size={15} />} onClick={() => setAttachFipOpen(true)}>
            Attach floating IP
          </Button>
          <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAttachOpen(true)}>
            Attach NIC
          </Button>
        </div>
      }
    >
      {/* Empty placeholder renders inside the table's own bordered frame (via
          `emptyState`), so the zone keeps the same frame whether empty or full. */}
      <DataTable
        columns={columns}
        data={nics}
        getRowId={(n, i) => String(n.nic_id ?? i)}
        emptyState={
          <EmptyState
            icon={<Network size={28} />}
            title="No network interfaces"
            description="This instance has no NICs, so it can't receive external traffic."
            actions={
              <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAttachOpen(true)}>
                Attach NIC
              </Button>
            }
          />
        }
      />

      <AttachNicDialog
        open={attachOpen}
        onOpenChange={setAttachOpen}
        serviceId={serviceId}
        instanceId={instance.id}
      />

      <AttachFloatingIpDialog
        open={attachFipOpen}
        onOpenChange={setAttachFipOpen}
        serviceId={serviceId}
        instanceId={instance.id}
      />

      <ConfirmDialog
        open={!!detachNic}
        onOpenChange={(o) => !o && setDetachNic(null)}
        title="Detach NIC?"
        description={`Detach the interface on “${detachNic?.network || ''}” (${detachNic?.ip || ''}) from this instance?`}
        confirmLabel="Detach"
        destructive
        loading={detach.isPending}
        onConfirm={() => detachNic?.nic_id != null && detach.mutate({ network_id: detachNic.nic_id })}
      />
    </Section>
    {manageNic && (
      <ManageNicSecgroupsDialog
        serviceId={serviceId}
        instanceId={instance.id}
        nic={manageNic}
        allGroups={allGroups}
        onClose={() => setManageNicId(null)}
      />
    )}
    </div>
  );
}

/**
 * Per-NIC security-group management. OpenNebula applies secgroups to a specific
 * NIC (network), not the whole instance — so attach/detach is scoped to this
 * NIC's `nic_id` (sent as `network_id`).
 */
function ManageNicSecgroupsDialog({
  serviceId,
  instanceId,
  nic,
  allGroups,
  onClose,
}: {
  serviceId: string;
  instanceId: string;
  nic: InstanceNicMeta;
  allGroups: Secgroup[];
  onClose: () => void;
}) {
  const { show } = useToast();
  const networkId = nic.nic_id ?? '';
  const currentIds = nic.security_groups ?? [];
  const available = allGroups.filter((g) => !currentIds.some((id) => String(id) === String(g.id)));
  const [pick, setPick] = useState('');
  const nameOf = (id: string) => allGroups.find((g) => String(g.id) === String(id))?.name || id;

  const attach = useAttachSecgroupToNic(serviceId, instanceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Security group attached' }); setPick(''); },
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });
  const detach = useDetachSecgroupFromNic(serviceId, instanceId, {
    onSuccess: () => show({ tone: 'success', title: 'Security group detached' }),
    onError: () => show({ tone: 'danger', title: 'Detach failed' }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Security groups</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ marginTop: 0, marginBottom: 'var(--vn-space-4)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            Firewall rule sets applied to <strong style={{ color: 'var(--vn-fg-default)' }}>{nic.network || `NIC ${networkId}`}</strong>
            {nic.ip ? <> · <code style={{ fontSize: 'var(--vn-font-size-sm)' }}>{nic.ip}</code></> : null}
          </p>

          {currentIds.length === 0 ? (
            <EmptyState icon={<ShieldOff size={20} />} title="No security groups" description="No firewall rule sets are applied to this interface." />
          ) : (
            <div style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflow: 'hidden' }}>
              {currentIds.map((id, i) => (
                <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-3)', borderTop: i ? '1px solid var(--vn-border-subtle, var(--vn-border-default))' : 'none' }}>
                  <span style={secgroupIcon}><ShieldCheck size={15} /></span>
                  <span style={{ fontWeight: 500, fontSize: 'var(--vn-font-size-sm)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(id)}</span>
                  <IconButton variant="ghost" size="sm" aria-label={`Detach ${nameOf(id)}`} disabled={detach.isPending} onClick={() => detach.mutate({ networkId, policyId: String(id) })}>
                    <Trash2 size={15} />
                  </IconButton>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 'var(--vn-space-4)' }}>
            <Label style={{ display: 'block', marginBottom: 'var(--vn-space-2)' }}>Add a security group</Label>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
              <Select value={pick} onValueChange={setPick} disabled={available.length === 0}>
                <SelectTrigger style={{ flex: 1 }}>
                  <SelectValue placeholder={available.length ? 'Select a security group' : 'All security groups attached'} />
                </SelectTrigger>
                <SelectContent>
                  {available.map((g) => <SelectItem key={g.id} value={String(g.id)}>{g.name || g.id}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button startIcon={<Plus size={15} />} disabled={!pick || attach.isPending} onClick={() => attach.mutate({ networkId, policyId: pick })}>Add</Button>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const secgroupIcon: React.CSSProperties = {
  display: 'grid',
  placeItems: 'center',
  width: 30,
  height: 30,
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-primary-subtle, var(--vn-bg-muted))',
  color: 'var(--vn-primary-text, var(--vn-fg-muted))',
  flexShrink: 0,
};

function CopyCell({ value }: { value?: string }) {
  const { show } = useToast();
  if (!value) return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={`Copy ${value}`}
        onClick={async () => {
          if (await copyToClipboard(value)) show({ tone: 'success', title: 'Copied' });
        }}
      >
        <Copy size={13} />
      </IconButton>
    </span>
  );
}

function AttachFloatingIpDialog({
  open,
  onOpenChange,
  serviceId,
  instanceId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  instanceId: string;
}) {
  const { show } = useToast();
  const { data: ips = [], isLoading } = useElasticIps(open ? serviceId : undefined);
  const [ipId, setIpId] = useState('');
  // Only unattached elastic IPs can be assigned as a floating (1:1 NAT) IP.
  const available = ips.filter((ip) => !ip.instance_id);

  const attach = useAttachElasticIp(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Floating IP attach requested' });
      onOpenChange(false);
      setIpId('');
    },
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach floating IP</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Alert tone="warning" style={{ marginBottom: 'var(--vn-space-3)' }}>
            A floating IP consumes an additional public IP address and may increase your monthly
            cost. Attach one only if this instance needs dedicated external access.
          </Alert>
          <Field label="Elastic IP">
            {(p) => (
              <Select value={ipId} onValueChange={setIpId} disabled={isLoading || available.length === 0}>
                <SelectTrigger {...p} style={{ width: '100%' }}>
                  <SelectValue
                    placeholder={
                      isLoading ? 'Loading…' : available.length ? 'Select an elastic IP' : 'No unattached elastic IPs'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {available.map((ip) => (
                    <SelectItem key={ip.id} value={ip.id}>
                      {ip.ip || ip.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!ipId || attach.isPending}
            onClick={() => attach.mutate({ id: ipId, instance_id: instanceId, use_type: 'floating' })}
          >
            Attach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachNicDialog({
  open,
  onOpenChange,
  serviceId,
  instanceId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  instanceId: string;
}) {
  const { show } = useToast();
  const { data: networks = [], isLoading } = useNetworkList(open ? serviceId : undefined);
  const [networkId, setNetworkId] = useState('');

  const attach = useAttachNic(serviceId, instanceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'NIC attach requested' });
      onOpenChange(false);
      setNetworkId('');
    },
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach network interface</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="Network">
            {(p) => (
              <Select value={networkId} onValueChange={setNetworkId} disabled={isLoading}>
                <SelectTrigger {...p} style={{ width: '100%' }}>
                  <SelectValue placeholder={isLoading ? 'Loading…' : 'Select a network'} />
                </SelectTrigger>
                <SelectContent>
                  {networks.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.name || n.id}
                      {n.cidr ? ` (${n.cidr})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!networkId || attach.isPending}
            onClick={() => attach.mutate({ network_id: networkId })}
          >
            Attach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
